import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export default function AdminSurveys() {
  const navigate = useNavigate();
  const token = localStorage.getItem('doorly_admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  // ── STATE ──
  const [activeSurveyType, setActiveSurveyType] = useState('borrower'); // 'borrower' | 'partner' | 'diaries'

  // Doorly Diaries state
  const [diaries, setDiaries] = useState([]);
  const [diariesLoading, setDiariesLoading] = useState(false);

  // Borrower survey state
  const [borrowerSurveys, setBorrowerSurveys] = useState([]);
  const [borrowerStats, setBorrowerStats] = useState(null);
  const [borrowerLoading, setBorrowerLoading] = useState(false);
  const [borrowerInsights, setBorrowerInsights] = useState(null);
  const [generatingBorrowerInsights, setGeneratingBorrowerInsights] = useState(false);
  const [selectedBorrowerSurvey, setSelectedBorrowerSurvey] = useState(null);

  // Partner survey state — identical shape
  const [partnerSurveys, setPartnerSurveys] = useState([]);
  const [partnerStats, setPartnerStats] = useState(null);
  const [partnerLoading, setPartnerLoading] = useState(false);
  const [partnerInsights, setPartnerInsights] = useState(null);
  const [generatingPartnerInsights, setGeneratingPartnerInsights] = useState(false);
  const [selectedPartnerSurvey, setSelectedPartnerSurvey] = useState(null);

  // Shared filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStart, setFilterStart] = useState('');
  const [filterEnd, setFilterEnd] = useState('');

  // Reset filters when switching tabs
  const handleSwitchTab = (tab) => {
    setActiveSurveyType(tab);
    setSearchQuery('');
    setFilterStart('');
    setFilterEnd('');
  };

  // ── FETCH FUNCTIONS ──

  const fetchBorrowerSurveys = useCallback(async () => {
    setBorrowerLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/surveys`, { headers });
      const data = await res.json();
      const surveys = data.surveys || [];
      setBorrowerSurveys(surveys);

      // Unified classification: NPS 9-10 OR stars 4-5 = promoter, NPS 7-8 OR stars 3 = passive, rest = detractor
      const classify = (s) => {
        if (s.nps_score !== null && s.nps_score !== undefined && s.nps_score >= 0 && s.nps_score <= 10) {
          return s.nps_score >= 9 ? 'promoter' : s.nps_score >= 7 ? 'passive' : 'detractor';
        }
        const star = s.star_rating || s.overall_rating;
        if (star !== null && star !== undefined && star >= 1 && star <= 5) {
          return star >= 4 ? 'promoter' : star === 3 ? 'passive' : 'detractor';
        }
        return null;
      };
      const classified = surveys.map(s => ({ ...s, sentiment: classify(s) })).filter(s => s.sentiment);
      const promoters = classified.filter(s => s.sentiment === 'promoter').length;
      const detractors = classified.filter(s => s.sentiment === 'detractor').length;
      const passives = classified.filter(s => s.sentiment === 'passive').length;
      const nps = classified.length > 0 ? Math.round(((promoters - detractors) / classified.length) * 100) : null;
      const withRating = surveys.filter(s => (s.star_rating || s.overall_rating) > 0);
      const avgRating = withRating.length > 0
        ? (withRating.reduce((sum, r) => sum + (r.star_rating || r.overall_rating), 0) / withRating.length).toFixed(1)
        : null;
      setBorrowerStats({ total: surveys.length, nps, avg_rating: avgRating, promoters, detractors, passives });
    } catch (e) { console.error('fetch borrower surveys', e); }
    finally { setBorrowerLoading(false); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPartnerSurveys = useCallback(async () => {
    setPartnerLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/surveys/partner`, { headers });
      const data = await res.json();
      setPartnerSurveys(data.surveys || []);
      setPartnerStats({
        total: data.total || 0,
        nps: data.nps,
        avg_rating: data.avg_rating,
        promoters: data.promoters || 0,
        detractors: data.detractors || 0,
        passives: data.passives || 0,
      });
    } catch (e) { console.error('fetch partner surveys', e); }
    finally { setPartnerLoading(false); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDiaries = useCallback(async () => {
    setDiariesLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/doorly-diaries`, { headers });
      const data = await res.json();
      setDiaries(data.diaries || []);
    } catch {}
    setDiariesLoading(false);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchBorrowerSurveys(); }, [fetchBorrowerSurveys]);
  useEffect(() => { if (activeSurveyType === 'partner' && partnerSurveys.length === 0 && !partnerStats) fetchPartnerSurveys(); }, [activeSurveyType]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (activeSurveyType === 'diaries' && diaries.length === 0) fetchDiaries(); }, [activeSurveyType]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── SHARED SURVEY SECTION COMPONENT ──

  const SurveySection = ({
    surveys, stats, loading, insights, setInsights,
    generating, setGenerating, selected, setSelected,
    generateEndpoint, surveyType,
  }) => {

    // Apply filters
    const filtered = surveys.filter(s => {
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const name = (s.first_name || s.partner_name || '') + ' ' + (s.last_name || '');
        const email = s.email || s.partner_email || '';
        if (!name.toLowerCase().includes(q) && !email.toLowerCase().includes(q)) return false;
      }
      if (filterStart && new Date(s.submitted_at || s.created_at) < new Date(filterStart)) return false;
      if (filterEnd && new Date(s.submitted_at || s.created_at) > new Date(filterEnd + 'T23:59:59')) return false;
      return true;
    });

    const handleGenerateInsights = async () => {
      setGenerating(true);
      try {
        const res = await fetch(`${API}${generateEndpoint}`, {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
        });
        const data = await res.json();
        if (res.ok && data.insights) setInsights(data.insights);
        else alert('Error: ' + (data.error || 'Could not generate insights'));
      } catch (e) { alert('Network error: ' + e.message); }
      finally { setGenerating(false); }
    };

    return (
      <div style={{ flex: 1 }}>

        {/* KPI Cards */}
        {stats && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '20px' }}>
            {[
              { label: 'Total Responses', value: stats.total, color: '#3a6ad9', bg: '#eff6ff' },
              {
                label: 'NPS Score',
                value: stats.nps !== null && stats.nps !== undefined ? stats.nps : '—',
                  color: stats.nps >= 50 ? '#2d6a0a' : stats.nps >= 0 ? '#7a5200' : '#c00520',
                bg: stats.nps >= 50 ? '#f4fee8' : stats.nps >= 0 ? '#fff7ed' : '#fff0f3',
              },
              { label: 'Avg Rating', value: stats.avg_rating ? stats.avg_rating + '/5' : '—', color: '#3a6ad9', bg: '#edf1fb' },
              { label: 'Promoters', value: stats.promoters || 0, icon: '🎯', color: '#2d6a0a', bg: '#f4fee8' },
            ].map(kpi => (
              <div key={kpi.label} style={{ background: kpi.bg, border: `1px solid ${kpi.color}25`, borderRadius: '12px', padding: '16px 18px' }}>
                <div style={{ fontSize: '24px', fontWeight: '800', color: kpi.color, marginBottom: '4px' }}>{kpi.value}</div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>{kpi.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Filter bar + AI Insights button in same row */}
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', marginBottom: '16px', flexWrap: 'wrap' }}>
          {/* Search */}
          <div style={{ position: 'relative', flex: 1, minWidth: '180px' }}>
            <label style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', display: 'block', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Search</label>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: '13px' }}>⌕</span>
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                placeholder="Name or email..."
                style={{ width: '100%', padding: '8px 10px 8px 30px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box', outline: 'none' }} />
            </div>
          </div>
          {/* Date From */}
          <div>
            <label style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', display: 'block', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>From</label>
            <input type="date" value={filterStart} onChange={e => setFilterStart(e.target.value)}
              style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', outline: 'none', cursor: 'pointer' }} />
          </div>
          {/* Date To */}
          <div>
            <label style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', display: 'block', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>To</label>
            <input type="date" value={filterEnd} onChange={e => setFilterEnd(e.target.value)}
              style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', outline: 'none', cursor: 'pointer' }} />
          </div>
          {/* Clear */}
          {(searchQuery || filterStart || filterEnd) && (
            <button onClick={() => { setSearchQuery(''); setFilterStart(''); setFilterEnd(''); }}
              style={{ padding: '8px 12px', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', alignSelf: 'flex-end' }}>
              ✕ Clear
            </button>
          )}
          {/* Result count */}
          <div style={{ alignSelf: 'flex-end', fontSize: '13px', color: '#6b7280' }}>
            <strong>{filtered.length}</strong> of {surveys.length}
          </div>
          {/* Generate AI Insights — pushed to right */}
          <div style={{ marginLeft: 'auto', alignSelf: 'flex-end' }}>
            <button
              onClick={handleGenerateInsights}
              disabled={generating || surveys.length === 0}
              style={{
                padding: '9px 18px',
                background: surveys.length === 0 ? '#e5e7eb' : '#3a6ad9',
                color: surveys.length === 0 ? '#9ca3af' : '#fff',
                border: 'none', borderRadius: '8px',
                cursor: surveys.length === 0 ? 'not-allowed' : 'pointer',
                fontWeight: '700', fontSize: '13px',
                display: 'inline-flex', alignItems: 'center', gap: '6px',
              }}
            >
              {generating ? 'Generating...' : 'Generate AI Insights'}
            </button>
          </div>
        </div>

        {/* AI Insights output */}
        {insights && (
          <div style={{ background: '#edf1fb', border: '1px solid #c5d3f5', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#2854c0' }}>AI Insights</h4>
              <button onClick={() => setInsights(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '16px' }}>✕</button>
            </div>
            {insights.summary && (
              <p style={{ margin: '0 0 14px 0', fontSize: '13px', color: '#374151', lineHeight: '1.7', background: '#fff', padding: '12px 14px', borderRadius: '8px' }}>
                {insights.summary}
              </p>
            )}
            {insights.nps_analysis && (
              <p style={{ margin: '0 0 14px 0', fontSize: '13px', color: '#374151', lineHeight: '1.7' }}>
                <strong>NPS Analysis:</strong> {insights.nps_analysis}
              </p>
            )}
            {insights.recommended_actions?.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '12px', fontWeight: '700', color: '#2854c0', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Recommended Actions</div>
                {insights.recommended_actions.map((action, i) => (
                  <div key={i} style={{ background: '#fff', border: '1px solid #c5d3f5', borderRadius: '8px', padding: '10px 14px', marginBottom: '6px', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                    <span style={{ fontSize: '10px', fontWeight: '700', padding: '2px 7px', borderRadius: '4px', background: action.priority === 'high' ? '#fff0f3' : '#fff7ed', color: action.priority === 'high' ? '#ff082f' : '#7a5200', flexShrink: 0, marginTop: '2px' }}>
                      {action.priority?.toUpperCase()}
                    </span>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827', marginBottom: '2px' }}>{action.action}</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>{action.rationale}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {insights.areas_of_concern?.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '12px', fontWeight: '700', color: '#c00520', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Areas of Concern</div>
                {insights.areas_of_concern.map((area, i) => (
                  <div key={i} style={{ background: '#fff0f3', border: '1px solid #fecdd3', borderRadius: '8px', padding: '10px 14px', marginBottom: '6px' }}>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#c00520', marginBottom: '2px' }}>{area.area}</div>
                    <div style={{ fontSize: '12px', color: '#374151' }}>{area.description}</div>
                  </div>
                ))}
              </div>
            )}
            {insights.positive_themes?.length > 0 && (
              <div>
                <div style={{ fontSize: '12px', fontWeight: '700', color: '#2d6a0a', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Positive Themes</div>
                {insights.positive_themes.map((theme, i) => (
                  <div key={i} style={{ background: '#f4fee8', border: '1px solid #d4f99a', borderRadius: '8px', padding: '10px 14px', marginBottom: '6px' }}>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#2d6a0a', marginBottom: '2px' }}>{theme.theme}</div>
                    <div style={{ fontSize: '12px', color: '#374151' }}>{theme.notes}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Responses table */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#9ca3af' }}>Loading surveys...</div>
        ) : filtered.length === 0 ? (
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '60px', textAlign: 'center', color: '#9ca3af' }}>
            <div style={{ fontSize: '32px', marginBottom: '12px', color: '#d1d5db' }}>—</div>
            <div style={{ fontWeight: '600', marginBottom: '4px' }}>No surveys found</div>
            <div style={{ fontSize: '13px' }}>
              {surveys.length === 0
                ? `No ${surveyType === 'partner' ? 'partner' : 'borrower'} survey responses yet`
                : 'No responses match your filters'}
            </div>
          </div>
        ) : (
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                  {['Respondent', 'NPS', 'Overall', 'Submitted', 'Feedback', ''].map(h => (
                    <th key={h} style={{ padding: '11px 16px', textAlign: 'left', fontSize: '11px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(survey => {
                  const name = survey.partner_name || `${survey.first_name || ''} ${survey.last_name || ''}`.trim() || 'Anonymous';
                  const email = survey.partner_email || survey.email || '';
                  const hasOpenFeedback = survey.open_feedback || survey.what_working_well || survey.what_could_improve || survey.feature_requests;
                  const nps = survey.nps_score;
                  return (
                    <tr key={survey.id} style={{ borderBottom: '1px solid #f3f4f6', cursor: hasOpenFeedback ? 'pointer' : 'default' }}
                      onClick={() => hasOpenFeedback && setSelected(survey)}
                      onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                      onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                    >
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>{name || '—'}</div>
                        <div style={{ fontSize: '11px', color: '#9ca3af' }}>{email}</div>
                        {survey.brokerage_name && <div style={{ fontSize: '11px', color: '#9ca3af' }}>{survey.brokerage_name}</div>}
                        {survey.loan_number && <div style={{ fontSize: '11px', color: '#9ca3af' }}>{survey.loan_number}</div>}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        {nps !== null && nps !== undefined ? (
                          <span style={{ fontSize: '15px', fontWeight: '800', color: nps >= 9 ? '#2d6a0a' : nps >= 7 ? '#7a5200' : '#ff082f' }}>
                            {nps}
                          </span>
                        ) : <span style={{ color: '#d1d5db', fontSize: '13px' }}>—</span>}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        {survey.overall_rating ? (
                          <div style={{ display: 'flex', gap: '2px' }}>
                            {[1,2,3,4,5].map(s => (
                              <span key={s} style={{ fontSize: '14px', filter: s <= survey.overall_rating ? 'none' : 'grayscale(1) opacity(0.2)' }}>⭐</span>
                            ))}
                          </div>
                        ) : <span style={{ color: '#d1d5db', fontSize: '13px' }}>—</span>}
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '12px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                        {new Date(survey.submitted_at || survey.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </td>
                      <td style={{ padding: '12px 16px', maxWidth: '200px' }}>
                        {hasOpenFeedback ? (
                          <div style={{ fontSize: '12px', color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {survey.open_feedback || survey.what_working_well || survey.what_could_improve || ''}
                          </div>
                        ) : <span style={{ color: '#d1d5db', fontSize: '12px' }}>—</span>}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        {hasOpenFeedback && (
                          <button onClick={e => { e.stopPropagation(); setSelected(survey); }}
                            style={{ padding: '4px 10px', background: '#f3f4f6', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', color: '#374151' }}>
                            View →
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Detail modal */}
        {selected && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
            onClick={() => setSelected(null)}>
            <div style={{ background: '#fff', borderRadius: '14px', padding: '28px', maxWidth: '540px', width: '100%', maxHeight: '80vh', overflow: 'auto' }}
              onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                <div>
                  <h3 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '700' }}>
                    {selected.partner_name || `${selected.first_name || ''} ${selected.last_name || ''}`.trim() || 'Anonymous'}
                  </h3>
                  <p style={{ margin: 0, fontSize: '12px', color: '#9ca3af' }}>
                    {selected.partner_email || selected.email} · {new Date(selected.submitted_at || selected.created_at).toLocaleDateString()}
                  </p>
                </div>
                <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#6b7280' }}>✕</button>
              </div>

              {/* Ratings grid */}
              {[selected.overall_rating, selected.platform_ease_rating, selected.nps_score].some(v => v !== null && v !== undefined) && (
                <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '16px' }}>
                  {[
                    { label: 'Overall', value: selected.overall_rating, max: 5 },
                    { label: 'NPS', value: selected.nps_score, max: 10 },
                    { label: 'Platform', value: selected.platform_ease_rating, max: 5 },
                  ].filter(f => f.value !== null && f.value !== undefined).map(f => (
                    <div key={f.label} style={{ background: '#f9fafb', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
                      <div style={{ fontSize: '20px', fontWeight: '800', color: '#111827' }}>{f.value}</div>
                      <div style={{ fontSize: '11px', color: '#6b7280' }}>{f.label} / {f.max}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Open feedback */}
              {[
                { label: '💬 Feedback', value: selected.open_feedback },
                { label: '🌟 What is working well', value: selected.what_working_well },
                { label: 'What could be better', value: selected.what_could_improve },
                { label: '🚀 Feature requests', value: selected.feature_requests },
              ].filter(f => f.value).map(f => (
                <div key={f.label} style={{ marginBottom: '12px', padding: '14px', background: '#f9fafb', borderRadius: '10px' }}>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: '#374151', marginBottom: '8px' }}>{f.label}</div>
                  <div style={{ fontSize: '14px', color: '#374151', lineHeight: '1.7' }}>{f.value}</div>
                </div>
              ))}

              {selected.user_id && (
                <button onClick={() => { setSelected(null); navigate(`/admin/borrowers/${selected.user_id}`); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: '#3a6ad9', fontWeight: '600', marginBottom: '12px' }}>
                  View Borrower Profile →
                </button>
              )}

              <button onClick={() => setSelected(null)}
                style={{ width: '100%', padding: '11px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '700', marginTop: '8px' }}>
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── MAIN RENDER ──

  return (
    <AdminLayout>
      <div style={{ display: 'flex', minHeight: 'calc(100vh - 64px)' }}>

        {/* Left panel */}
        <div style={{ width: '220px', flexShrink: 0, background: '#fff', borderRight: '1px solid #e5e7eb', padding: '24px 0' }}>
          <div style={{ padding: '0 16px 16px', fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Survey Source
          </div>
          {[
            { id: 'borrower', label: 'Doorly Web App', count: borrowerSurveys.length },
            { id: 'partner', label: 'DoorlyPro', count: partnerSurveys.length },
            { id: 'diaries', label: 'Doorly Diaries', count: diaries.length },
          ].map(item => (
            <button
              key={item.id}
              onClick={() => handleSwitchTab(item.id)}
              style={{
                width: '100%',
                padding: '10px 16px',
                background: activeSurveyType === item.id ? '#eff6ff' : 'transparent',
                border: 'none',
                borderLeft: `3px solid ${activeSurveyType === item.id ? '#3a6ad9' : 'transparent'}`,
                cursor: 'pointer',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                transition: 'all 0.15s',
              }}
            >
              <span style={{
                fontSize: '13px',
                fontWeight: activeSurveyType === item.id ? '700' : '500',
                color: activeSurveyType === item.id ? '#3a6ad9' : '#374151',
                flex: 1,
              }}>
                {item.label}
              </span>
              {item.count > 0 && (
                <span style={{
                  fontSize: '11px', fontWeight: '700',
                  background: activeSurveyType === item.id ? '#3a6ad9' : '#f3f4f6',
                  color: activeSurveyType === item.id ? '#fff' : '#6b7280',
                  padding: '1px 7px', borderRadius: '10px',
                }}>
                  {item.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Main content */}
        <div style={{ flex: 1, padding: '24px', overflow: 'auto', background: '#f9fafb' }}>

          {/* Page header */}
          <div style={{ marginBottom: '24px' }}>
            <h1 style={{ margin: '0 0 4px 0', fontSize: '22px', fontWeight: '700', color: '#111827' }}>
              {activeSurveyType === 'borrower' ? 'Doorly Web App Surveys' : activeSurveyType === 'partner' ? 'DoorlyPro Partner Surveys' : 'Doorly Diaries'}
            </h1>
            <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>
              {activeSurveyType === 'borrower'
                ? 'Feedback from borrowers using the Doorly borrower portal'
                : 'Feedback from DoorlyPro partner agents'}
            </p>
          </div>

          {/* Render the shared section for active tab */}
          {activeSurveyType === 'diaries' ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <span style={{ fontSize: 24 }}>📖</span>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: '#111827' }}>Doorly Diaries</div>
                  <div style={{ fontSize: 13, color: '#6b7280' }}>Homeownership stories from closed borrowers</div>
                </div>
                <span style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 700, color: '#3a6ad9', background: '#edf1fb', padding: '4px 12px', borderRadius: 8 }}>{diaries.length} stories</span>
              </div>
              {diariesLoading ? (
                <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Loading...</div>
              ) : diaries.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 48, color: '#9ca3af', background: '#f9fafb', borderRadius: 12 }}>No stories submitted yet.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {diaries.map(d => (
                    <div key={d.id} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '18px 20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>{d.first_name} {d.last_name}</div>
                        {d.loan_number && <span style={{ fontSize: 11, color: '#9ca3af' }}>Loan #{d.loan_number}</span>}
                        <span style={{ fontSize: 11, color: '#d1d5db', marginLeft: 'auto' }}>{new Date(d.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                      </div>
                      {d.email && <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>{d.email}</div>}
                      {d.story && (
                        <div style={{ fontSize: 14, color: '#374151', lineHeight: 1.7, padding: '12px 14px', background: '#f9fafb', borderRadius: 8, fontStyle: 'italic' }}>
                          "{d.story}"
                        </div>
                      )}
                      {d.photo_url && (
                        <img src={d.photo_url} alt="Home" style={{ marginTop: 10, maxHeight: 160, borderRadius: 8, objectFit: 'cover' }} />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : activeSurveyType === 'borrower' ? (
            <SurveySection
              surveys={borrowerSurveys}
              stats={borrowerStats}
              loading={borrowerLoading}
              insights={borrowerInsights}
              setInsights={setBorrowerInsights}
              generating={generatingBorrowerInsights}
              setGenerating={setGeneratingBorrowerInsights}
              selected={selectedBorrowerSurvey}
              setSelected={setSelectedBorrowerSurvey}
              generateEndpoint="/api/admin/surveys/generate-insights"
              surveyType="borrower"
            />
          ) : (
            <SurveySection
              surveys={partnerSurveys}
              stats={partnerStats}
              loading={partnerLoading}
              insights={partnerInsights}
              setInsights={setPartnerInsights}
              generating={generatingPartnerInsights}
              setGenerating={setGeneratingPartnerInsights}
              selected={selectedPartnerSurvey}
              setSelected={setSelectedPartnerSurvey}
              generateEndpoint="/api/admin/surveys/partner/generate-insights"
              surveyType="partner"
            />
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
